import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import Manufacturer from '../../../types/manufacturer/Manufacturer';
import ManufacturerWriteDTO from '../../../types/manufacturer/ManufacturerWriteDTO';
import Page from '../../../types/shared/Page';
import { BaseService } from './base.service';

@Injectable({
    providedIn: 'root'
})
export class ManufacturerService {

    private RESOURCE: string = 'manufacturer';

    constructor(private baseService: BaseService) {
    }

    /**
     * Fetches the manufacturers from the backend.
     *
     * @param search The search query to filter the manufacturers by.
     * @param page The page number to fetch.
     * @param size The number of elements to fetch per page.
     * @return The manufacturers.
     */
    public async getManufacturers(search?: string, page: number = 0, size: number = 10): Promise<Page<Manufacturer>> {
        let params = new HttpParams().set('page', page.toString()).set('size', size.toString());
        if (search) {
            params = params.set('search', search);
        }

        return await this.baseService.get<Page<Manufacturer>>(this.RESOURCE, params);
    }

    /**
     * Fetches multiple manufacturers from the backend.
     *
     * @param ids The IDs of the manufacturers to fetch.
     * @return The manufacturers.
     */
    public async getMultipleManufacturers(ids: number[]): Promise<Manufacturer[]> {
        const params = new HttpParams().set('ids', ids.join(','));

        return await this.baseService.get<Manufacturer[]>(this.RESOURCE + '/multiple', params);
    }

    /**
     * Fetches all manufacturers from the backend.
     *
     * @return The manufacturers.
     */
    public async getAllManufacturers(): Promise<Manufacturer[]> {
        return await this.baseService.get<Manufacturer[]>(this.RESOURCE + '/all');
    }

    /**
     * Gets a manufacturer by its ID.
     *
     * @param id The ID of the manufacturer to fetch.
     * @return The manufacturer.
     */
    public async getManufacturer(id: number): Promise<Manufacturer> {
        return await this.baseService.get<Manufacturer>(this.RESOURCE + '/' + id);
    }

    /**
     * Creates a manufacturer in the backend.
     *
     * @param manufacturer The manufacturer to create.
     * @return The created manufacturer.
     */
    public async createManufacturer(manufacturer: ManufacturerWriteDTO): Promise<void> {
        return await this.baseService.post<void>(this.RESOURCE, manufacturer);
    }

    /**
     * Updates a manufacturer in the backend.
     *
     * @param manufacturer The manufacturer to update.
     * @return The response from the request.
     */
    public async updateManufacturer(manufacturer: ManufacturerWriteDTO): Promise<void> {
        return await this.baseService.put<void>(this.RESOURCE + '/' + manufacturer.id, manufacturer);
    }

    /**
     * Deletes a manufacturer from the backend.
     *
     * @param id The ID of the manufacturer to delete.
     * @return The response from the request.
     */
    public async deleteManufacturer(id: number): Promise<void> {
        return await this.baseService.delete<void>(this.RESOURCE + '/' + id);
    }
}
